import React, { useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../components/ConfirmPopup';
import { showToast, removeToast } from '../context/ToastHandle';
import api from '../services/api';
import { getAccountType, getBoardLimit, getDecodedToken, getBoardUsage } from '../context/accountUtils';
import ShareBoardButton from '../components/ShareBoardButton';
import Popup from '../components/Popup';
import StickyCoreSettings, { loadStickyCoreCache, saveStickyCoreCache } from '../components/StickyCoreSettings';
import { clearBoardCaches } from '../context/cacheUtils';

const ItemTypes = { FOLDER: 'folder', BOARD: 'board' };
const UNSORTED = 'unsorted';

// Drag/drop is intentionally optimistic: UI updates immediately, while saves
// are coalesced and paced so a large folder cannot create a burst of database
// connections while the pointer is still hovering between items.
const ORDER_SAVE_DEBOUNCE_MS = 850;
const ORDER_SAVE_REQUEST_GAP_MS = 85;
const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const cacheBoardConfig = async (cache, board) => {
  const configCacheKey = `/aac/api/getconfig.php?id=${board.id}`;
  const configPayload = {
    id: board.id,
    name: board.name || '',
    grid_config:
      board.grid_config || JSON.stringify({ rows: 5, columns: 8 }),
    scrollable: board.scrollable ?? 0,
    square_buttons: board.square_buttons ?? 1,
    theme: board.theme || 'default',
  };

  const response = new Response(JSON.stringify(configPayload), {
    status: 200,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      Date: new Date().toUTCString(),
    },
  });

  await cache.put(configCacheKey, response);
};

const cacheBoardTiles = async (cache, boardId, tiles) => {
  const response = new Response(JSON.stringify(Array.isArray(tiles) ? tiles : []), {
    status: 200,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      Date: new Date().toUTCString(),
    },
  });

  await cache.put(`/aac/api/tiles.php?board_id=${boardId}`, response);
};

const cacheJsonResponse = async (cache, key, payload) => {
  const response = new Response(JSON.stringify(payload), {
    status: 200,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      Date: new Date().toUTCString(),
    },
  });

  await cache.put(key, response);
};

const prefetchImages = async (urls, concurrency = 8) => {
  const uniqueUrls = [...new Set((urls || []).filter(Boolean))];
  let cursor = 0;

  const worker = async () => {
    while (cursor < uniqueUrls.length) {
      const url = uniqueUrls[cursor++];

      try {
        await fetch(url);
      } catch {
        console.debug('Image prefetch failed:', url);
      }
    }
  };

  const workerCount = Math.min(concurrency, uniqueUrls.length);
  await Promise.allSettled(
    Array.from({ length: workerCount }, () => worker())
  );
};



/* Overlay spinner that sits over the boards/folders section */
const LoadingOverlay = ({ show, text = 'Loading…' }) => {
  if (!show) return null;
  return (
    <div style={overlayStyles.container}>
      <div style={overlayStyles.card}>
        <div className="spinner" />
        <p style={{ marginTop: 8 }}>{text}</p>
      </div>
    </div>
  );
};

/* --- Folder component --- */
const Folder = ({
  folder,
  index,
  moveFolder,
  children,
  toggleExpand,
  isExpanded,
  onDropBoard,
  boardCountInFolder,
  fetchBoards,
  boardNames,
  manageMode,
  selected,
  someSelected,
  onToggleSelected,
}) => {
  const headerRef = useRef(null);
  const selectRef = useRef(null);
  const { showConfirm } = useConfirm();

  useEffect(() => {
    if (selectRef.current) {
      selectRef.current.indeterminate = Boolean(someSelected && !selected);
    }
  }, [someSelected, selected]);

  // Allow dropping folders on folders (reorder) – with proper half-height threshold
  const [, dropFolder] = useDrop({
    accept: ItemTypes.FOLDER,
    hover: (dragged, monitor) => {
      if (dragged.index === index) return;
      const node = headerRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const hoverMiddleY = (rect.bottom - rect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - rect.top;

      if (dragged.index < index && hoverClientY < hoverMiddleY) return;
      if (dragged.index > index && hoverClientY > hoverMiddleY) return;

      moveFolder(dragged.index, index);
      dragged.index = index;
    }
  });

  // Allow dropping boards on the folder header (move into end of folder).
  // IMPORTANT: handle only when shallowly over the header and if no child already handled the drop.
  const [, dropBoard] = useDrop({
    accept: ItemTypes.BOARD,
    drop: (dragged, monitor) => {
      if (monitor.didDrop()) return; // child handled it
      if (!monitor.isOver({ shallow: true })) return; // only header area
      onDropBoard(dragged.folderId, folder.id, dragged.index, dragged.id);
      dragged.folderId = folder.id;
      dragged.index = boardCountInFolder;
      return { handled: true };
    }
  });

  const [, dragFolder] = useDrag({
    type: ItemTypes.FOLDER,
    item: { type: ItemTypes.FOLDER, id: folder.id, index },
    canDrag: !manageMode,
  });

  const [isRenaming, setIsRenaming] = useState(false);
  const [name, setName] = useState(folder.name);

  const saveRename = async () => {
    try {
      await api.put(`/folders.php`, { id: folder.id, name });
      fetchBoards();
    } catch (err) {
      console.error('Rename folder failed', err);
    }
    setIsRenaming(false);
  };

  const handleDeleteFolder = async () => {
    const confirmed = await showConfirm('Delete this folder? All boards will move to Unsorted.');
    if (confirmed) {
      try {
        await api.delete(`/folders.php?id=${folder.id}`);
        showToast('Folder deleted successfully.', 'success', false);
        fetchBoards();
      } catch (err) {
        console.error('Failed to delete folder:', err);
        showToast('Failed to delete folder.', 'error', false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveRename();
    if (e.key === 'Escape') {
      setName(folder.name);
      setIsRenaming(false);
    }
  };

  return (
    <div className="folder-container">
      <div
        ref={(node) => {
          headerRef.current = node;
          dragFolder(dropFolder(node));
          dropBoard(node);
        }}
        className="folder-header"
      >
        {manageMode && (
          <input
            ref={selectRef}
            type="checkbox"
            checked={Boolean(selected)}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelected(folder.id, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select folder ${folder.name}`}
            style={{
              width: 18,
              height: 18,
              marginRight: 10,
              accentColor: '#1976d2',
              cursor: 'pointer',
              flex: '0 0 auto',
            }}
          />
        )}

        {isRenaming ? (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveRename}
            onKeyDown={handleKeyDown}
            autoFocus
            className="folder-rename-input"
          />
        ) : (
          <div className="folder-name" onClick={() => setIsRenaming(true)}>{folder.name}</div>
        )}
        <div className="folder-toggle" onClick={() => toggleExpand(folder.id)}>
          {isExpanded ? '📂' : '📁'}
        </div>
        {!manageMode && (
        <button
          onClick={handleDeleteFolder}
          style={{ marginLeft: 'auto', color: 'red', fontSize: '20px', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          🗑
        </button>
        )}
      </div>
      {!isExpanded && boardNames?.length > 0 && (
        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px', paddingLeft: '12px' }}>
          {(() => {
            const preview = boardNames.join(', ');
            return preview.length > 50 ? preview.slice(0, 47) + '...' : preview;
          })()}
        </div>
      )}
      {isExpanded && <div className="folder-children">{children}</div>}
    </div>
  );
};

/* --- UnsortedDropZone --- */
const UnsortedDropZone = ({
  onDropBoard,
  children,
  manageMode,
  onToggleManage,
  selectedCount,
  allSelected,
  onToggleSelectAll,
  onDeleteSelected,
  onDuplicateSelected,
  onMoveSelected,
  canMoveSelected,
  bulkBusy,
}) => {
  const [, drop] = useDrop({
    accept: ItemTypes.BOARD,
    drop: (dragged, monitor) => {
      // Only handle if no child handled and we are shallowly over the zone.
      if (monitor.didDrop()) return;
      if (!monitor.isOver({ shallow: true })) return;
      if (manageMode) return;
      onDropBoard(dragged.folderId, UNSORTED, dragged.index, dragged.id);
      dragged.folderId = UNSORTED;
      return { handled: true };
    }
  });

  const isEmpty =
    !children || // nothing rendered
    (Array.isArray(children) && children.length === 0); // empty array

  return (
    <div ref={drop} style={{ marginTop: '2rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: '0.75rem',
        }}
      >
        <h3 style={{ margin: 0 }}>Boards</h3>
        <button
          type="button"
          onClick={onToggleManage}
          disabled={bulkBusy}
          style={{
            border: manageMode ? '1px solid #a9bfdc' : '1px solid #d7dce3',
            background: manageMode ? '#eef4fb' : '#fff',
            color: manageMode ? '#244b73' : '#4b5563',
            borderRadius: 999,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: bulkBusy ? 'not-allowed' : 'pointer',
          }}
        >
          {manageMode ? 'Done' : 'Manage'}
        </button>
      </div>

      {manageMode && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '10px',
            marginBottom: '12px',
            background: '#f7f9fc',
            border: '1px solid #e0e5eb',
            borderRadius: 10,
          }}
        >
          <button
            type="button"
            onClick={onToggleSelectAll}
            disabled={bulkBusy}
            style={manageActionStyle('#fff', '#334155', '#cbd5e1')}
          >
            {allSelected ? 'Unselect all' : 'Select all'}
          </button>

          <button
            type="button"
            onClick={onDuplicateSelected}
            disabled={bulkBusy || selectedCount === 0}
            style={manageActionStyle(
              selectedCount ? '#eef6ff' : '#f4f5f6',
              selectedCount ? '#1d5d96' : '#9aa1aa',
              selectedCount ? '#bdd7ee' : '#e0e3e6'
            )}
          >
            Duplicate selected
          </button>

          <button
            type="button"
            onClick={onMoveSelected}
            disabled={bulkBusy || selectedCount === 0 || !canMoveSelected}
            style={manageActionStyle(
              selectedCount && canMoveSelected ? '#f0f7f4' : '#f4f5f6',
              selectedCount && canMoveSelected ? '#24654f' : '#9aa1aa',
              selectedCount && canMoveSelected ? '#bdd9cf' : '#e0e3e6'
            )}
          >
            Move selected
          </button>

          <button
            type="button"
            onClick={onDeleteSelected}
            disabled={bulkBusy || selectedCount === 0}
            style={manageActionStyle(
              selectedCount ? '#fff1f1' : '#f4f5f6',
              selectedCount ? '#b42318' : '#9aa1aa',
              selectedCount ? '#efc3c0' : '#e0e3e6'
            )}
          >
            Delete selected
          </button>

          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280', fontWeight: 650 }}>
            {bulkBusy ? 'Working…' : `${selectedCount} selected`}
          </span>
        </div>
      )}

      {isEmpty ? (
        <i style={{ color: '#666' }}>You can drop unsorted boards back here...</i>
      ) : (
        children
      )}
    </div>
  );
};

/* --- Board card (adds drop() so parent zones don't re-handle) --- */
const Board = ({
  board,
  index,
  folderId,
  moveBoard,
  onClickEdit,
  onClickUse,
  fetchBoards,
  boardsByFolder,
  setBoardsByFolder,
  manageMode,
  selected,
  onToggleSelected,
}) => {
  const ref = useRef(null);
  const { showConfirm } = useConfirm();

  const [, drop] = useDrop({
    accept: ItemTypes.BOARD,
    hover: (dragged, monitor) => {
      if (manageMode) return;
      if (!ref.current) return;

      const dragIndex = dragged.index;
      const hoverIndex = index;
      const dragFolder = dragged.folderId;
      const hoverFolder = folderId;

      // Nothing to do
      if (dragIndex === hoverIndex && dragFolder === hoverFolder) return;

      const rect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (rect.bottom - rect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - rect.top;

      if (dragFolder === hoverFolder) {
        // Move only after crossing half the item height (prevents jitter)
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      }

      moveBoard(dragFolder, hoverFolder, dragIndex, hoverIndex, dragged.id);
      dragged.index = hoverIndex;
      dragged.folderId = hoverFolder;
    },
    // Add a drop handler so ancestors see didDrop() === true and don't also run.
    drop: () => ({ handled: true })
  });

  const [, drag] = useDrag({
    type: ItemTypes.BOARD,
    item: { type: ItemTypes.BOARD, id: board.id, index, folderId },
    canDrag: !manageMode,
  });

  drag(drop(ref));

  const [isRenaming, setIsRenaming] = useState(false);
  const [name, setName] = useState(board.name);

  const saveRename = async () => {
    try {
      await api.put(`/boards.php?id=${board.id}`, { name });
      fetchBoards();
    } catch (err) {
      console.error('Rename failed', err);
      showToast('Failed to rename board', 'error', false);
    }
    setIsRenaming(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveRename();
    if (e.key === 'Escape') {
      setName(board.name);
      setIsRenaming(false);
    }
  };

  const isOffline = board.is_cached;

  const handleDuplicate = async () => {
    const limit = getBoardLimit();
    const totalBoards = Object.values(boardsByFolder).reduce((sum, list) => sum + list.length, 0);
    if (totalBoards >= limit) {
      showToast(`🚫 Board limit reached (${limit}). Upgrade your plan to add more.`, 'error', false);
      return;
    }
    try {
      await api.post(`/boards.php?duplicate=${board.id}`);
      showToast('Board duplicated successfully!', 'success', false);
      fetchBoards();
    } catch (err) {
      console.error('Failed to duplicate board:', err);
      showToast('Failed to duplicate board', 'error', false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm('Are you sure you want to delete this board?');
    if (confirmed) {
      try {
        await api.delete(`/boards.php?id=${board.id}`);

        setBoardsByFolder(prev => {
          const next = {};
          for (const [folderId, arr] of Object.entries(prev)) {
            next[folderId] = (arr || []).filter(b => String(b.id) !== String(board.id));
          }
          return next;
        });

        if (navigator.onLine) {
          try {
            const cache = await caches.open('api-cache');
            await Promise.all([
              cache.delete('/aac/api/boards.php'),
              cache.delete('/aac/api/folders.php'),
            ]);
          } catch{}
        }


        await clearBoardCaches(board.id); 

        window.dispatchEvent(new CustomEvent('sticky-core-changed'));
        showToast('Deleted successfully!', 'success', false);
        fetchBoards();
      } catch (err) {
        showToast('Failed to delete board', 'error', false);
        console.error('Failed to delete board:', err);
      }
    }
  };

  return (
    <div
      ref={ref}
      style={{
        border: '1px solid #ccc',
        borderRadius: '6px',
        padding: '1rem',
        marginBottom: '0.5rem',
        backgroundColor: '#fff',
        cursor: manageMode ? 'default' : 'grab',
        transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s',
        outline: selected ? '2px solid #7aa7d9' : 'none',
        outlineOffset: selected ? '-2px' : 0,
      }}
      className='boardCard'
    >
      {manageMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={Boolean(selected)}
            onChange={(e) => onToggleSelected(board.id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select board ${board.name || board.id}`}
            style={{
              width: 18,
              height: 18,
              accentColor: '#1976d2',
              cursor: 'pointer',
              flex: '0 0 auto',
            }}
          />
          <span style={{ fontSize: 12, fontWeight: 700, color: selected ? '#285b88' : '#6b7280' }}>
            {selected ? 'Selected' : 'Select board'}
          </span>
        </div>
      )}

      {isRenaming ? (
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveRename}
          onKeyDown={handleKeyDown}
          autoFocus
          style={{ fontWeight: 'bold', marginBottom: '0.5rem', width: '100%' }}
        />
      ) : (
        <div
          style={{ fontWeight: 'bold', marginBottom: '0.5rem', cursor: manageMode ? 'default' : 'pointer' }}
          onClick={() => {
            if (!manageMode) setIsRenaming(true);
          }}
        >
          {board.name || `Board #${board.id}`}
        </div>
      )}

      {isOffline ? (
            <span className="badge badge--ok" title="Available offline">Available Offline</span>
      ) : (
            <span className="badge badge--warn" title="Not cached yet">Online only</span>
      )}

      <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
        Created: {new Date(board.created_at).toLocaleString()}
      </div>

      {!manageMode && (
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button onClick={() => onClickUse(board.id)} style={buttonStyle('#007bff')}>Use</button>
        <button onClick={() => onClickEdit(board.id)} style={buttonStyle('#6c757d')}>Edit</button>
        <button onClick={handleDuplicate} style={buttonStyle('#ffc107', '#000')}>Duplicate</button>
        <button onClick={handleDelete} style={buttonStyle('#dc3545')}>Delete</button>
        <ShareBoardButton boardId={board.id} boardName={board.name} />
      </div>
      )}
    </div>
  );
};

const BoardFolderList = () => {
  const { showConfirm } = useConfirm();

  const [folders, setFolders] = useState([]);
  const [boardsByFolder, setBoardsByFolder] = useState({});
  const [expanded, setExpanded] = useState({});
  const [newBoardName, setNewBoardName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [loading, setLoading] = useState(true);              // drives overlay
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [tutorialIsCached, setTutorialIsCached] = useState(false); // for tutorial card
  const [showPopup, setShowPopup] = useState(false);

  // Bulk manage mode
  const [manageMode, setManageMode] = useState(false);
  const [selectedBoardIds, setSelectedBoardIds] = useState(() => new Set());
  const [selectedFolderIds, setSelectedFolderIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showMovePopup, setShowMovePopup] = useState(false);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState('');

  // Reordering can fire dozens of hover events. Keep those changes local and
  // merge all affected rows into one pending save instead of writing on every hover.
  const pendingBoardOrderRef = useRef(new Map());
  const pendingFolderOrderRef = useRef(new Map());
  const boardOrderTimerRef = useRef(null);
  const folderOrderTimerRef = useRef(null);
  const orderSaveChainRef = useRef(Promise.resolve());


  // ⬇️ Tutorial additions
  const [tutorialSummary, setTutorialSummary] = useState(null); // {exists, title, tileCount}
  const userId = getDecodedToken()?.sub;
  const seenKey = `tutorialPromptSeen:${userId}`;
  const [showTutorialPrompt, setShowTutorialPrompt] = useState(() => !localStorage.getItem(seenKey));
  // ⬆️ Tutorial additions

  const navigate = useNavigate();

  const openBoard = async (boardId) => {
    const targetId = Number(boardId);

    try {
      let settings = null;

      // core-settings.php is intentionally NOT requested while offline.
      // The normal boards/tiles are already cached separately.
      if (!navigator.onLine) {
        settings = loadStickyCoreCache();
      } else {
        try {
          const { data } = await api.get('/core-settings.php');
          settings = data;
          saveStickyCoreCache(data);
        } catch (err) {
          settings = loadStickyCoreCache();
          if (!settings) throw err;
        }
      }

      const coreBoard = (settings?.core_boards || []).find(
        (board) => String(board.id) === String(targetId)
      );

      if (coreBoard?.core_default_board_id) {
        let updatedSettings;

        if (!navigator.onLine) {
          // Selecting/opening a Core Board must still work offline. Update the
          // local preference only; do not attempt an impossible PUT request.
          updatedSettings = {
            ...settings,
            enabled: true,
            board_id: targetId,
            side: settings?.side || 'left',
            selected_board: coreBoard,
          };
        } else {
          const response = await api.put('/core-settings.php', {
            action: 'settings',
            enabled: true,
            board_id: targetId,
            side: settings?.side || 'left',
          });
          updatedSettings = response.data;
        }

        saveStickyCoreCache(updatedSettings);
        window.dispatchEvent(
          new CustomEvent('sticky-core-changed', { detail: updatedSettings })
        );

        navigate(`/board/${coreBoard.core_default_board_id}`);
        return;
      }
    } catch (err) {
      console.debug('Could not resolve Core Board default link:', err);
    }

    navigate(`/board/${targetId}`);
  };

  useEffect(() => {
    if (getAccountType() !== 'unlimited') {
      navigate('/boards');
    } else {
      fetchFoldersAndBoards();
      fetchTutorialSummary(); // ⬅️ load tutorial summary alongside
    }
  }, []); // run once on mount

const fetchFoldersAndBoards = async () => {
  setLoading(true);
  const toastEl = showToast('Caching boards for offline use…', 'info', true);

  try {
    const syncRes = await api.get('/offline-sync.php');
    const syncData = syncRes?.data || {};

    const folderData = Array.isArray(syncData.folders) ? syncData.folders : [];
    const boardData = Array.isArray(syncData.boards) ? syncData.boards : [];
    const tilesByBoard =
      syncData.tiles && typeof syncData.tiles === 'object'
        ? syncData.tiles
        : {};

    const cache = await caches.open('api-cache');
    const now = Date.now();

    await Promise.all(
      boardData.map(async (board) => {
        const boardTiles = Array.isArray(tilesByBoard[String(board.id)])
          ? tilesByBoard[String(board.id)]
          : Array.isArray(tilesByBoard[board.id])
            ? tilesByBoard[board.id]
            : [];

        await Promise.all([
          cacheBoardConfig(cache, board),
          cacheBoardTiles(cache, board.id, boardTiles),
        ]);

        localStorage.setItem(`boardCacheAt:${board.id}`, now.toString());
      })
    );

    await Promise.all([
      cacheJsonResponse(cache, '/aac/api/boards.php', boardData),
      cacheJsonResponse(cache, '/aac/api/folders.php', folderData),
    ]);

    localStorage.setItem('boardsCachedAt', now.toString());

    const imageUrls = boardData.flatMap((board) => {
      const boardTiles = tilesByBoard[String(board.id)] || tilesByBoard[board.id] || [];
      return Array.isArray(boardTiles)
        ? boardTiles.map((tile) => tile?.image_url).filter(Boolean)
        : [];
    });

    await prefetchImages(imageUrls, 8);

    try {
      const TUTORIAL_STALE_MS = 1000 * 60 * 60 * 24 * 10;
      const tutorialCacheKey = '/aac/api/tutorial.php?tiles=1';
      const tutorialMatch = await cache.match(tutorialCacheKey);
      const lastTutorialPrefetch = Number(localStorage.getItem('tutorialCachedAt') || 0);
      const tutorialIsStale = now - lastTutorialPrefetch > TUTORIAL_STALE_MS;

      if (tutorialMatch && !tutorialIsStale) {
        setTutorialIsCached(true);
      } else {
        const tutRes = await api.get('/tutorial.php?tiles=1');
        const tutTiles = Array.isArray(tutRes.data) ? tutRes.data : [];

        await prefetchImages(
          tutTiles.map((tile) => tile?.image_url).filter(Boolean),
          8
        );

        setTutorialIsCached(true);
        localStorage.setItem('tutorialCachedAt', Date.now().toString());
      }
    } catch (e) {
      console.debug('Tutorial prefetch error:', e);
    }

    const updatedBoardData = boardData.map((board) => ({
      ...board,
      is_cached: true,
    }));

    const grouped = {};
    folderData.forEach((folder) => {
      grouped[folder.id] = updatedBoardData
        .filter((b) => String(b.folder_id) === String(folder.id))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    });

    grouped[UNSORTED] = updatedBoardData
      .filter((b) => !b.folder_id)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    setFolders(folderData);
    setBoardsByFolder(grouped);

    if (!hasLoadedOnce) {
      const initialExpanded = {};
      folderData.forEach((f) => (initialExpanded[f.id] = false));
      initialExpanded[UNSORTED] = true;
      setExpanded(initialExpanded);
      setHasLoadedOnce(true);
    }

    console.log(`Bulk offline sync cached ${boardData.length} boards`);
  } catch (err) {
    console.error('Failed to load folders or boards:', err);
  } finally {
    removeToast(toastEl);
    setLoading(false);
  }
};

function CommunityBoards({ onOpen }) {
  return (
    <div
      className="tutorial-card tutorial-card--clickable"
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
     <div className="tutorial-card__body">
        <div className="tutorial-card__header">
          <h4 className="tutorial-card__title">Community Boards</h4>
            <span className="badge badge--warn" title="Not cached yet">Online only</span>
        </div>

        <p className="tutorial-card__desc">
          Explore and use community boards created by other users.
        </p>
      </div>

      {/* ✅ Play icon on the right */}
      <img src="/aac/api/imgs/playbutton.png" alt="" className="tutorial-card__play" />
    </div>
  );
}

function PresetBoardsCard({ onOpen }) {
  return (
    <div className="tutorial-card tutorial-card--clickable" onClick={onOpen} role="button" tabIndex={0}>
      <div className="tutorial-card__body">
        <div className="tutorial-card__header">
          <h4 className="tutorial-card__title">Preset Boards</h4>
          <span className="badge badge--warn" title="Online library">Online library</span>
        </div>
        <div className="tutorial-card__meta">
          <div className="tutorial-card__desc">Search &amp; add ready-made AAC boards for everyday communication and learning.</div>
        </div>
      </div>
        {/* ✅ Play icon on the right */}
        <img src="/aac/api/imgs/playbutton.png" alt="" className="tutorial-card__play" />
    </div>
  );
}

function TutorialCard({ isCached, onOpen }) {
  return (
    <div
      className="tutorial-card tutorial-card--clickable"
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
     <div className="tutorial-card__body">
        <div className="tutorial-card__header">
          <h4 className="tutorial-card__title">Tutorial</h4>
          {isCached ? (
            <span className="badge badge--ok" title="Available offline">Available Offline</span>
          ) : (
            <span className="badge badge--warn" title="Not cached yet">Online only</span>
          )}
        </div>

        <p className="tutorial-card__desc">
          Learn how to navigate boards, edit tiles, and use speech quickly.
        </p>
      </div>

      {/* ✅ Play icon on the right */}
      <img src="/aac/api/imgs/playbutton.png" alt="" className="tutorial-card__play" />
    </div>
  );
}


  // ⬇️ Tutorial helpers
  const fetchTutorialSummary = async () => {
    try {
      const res = await api.get('/tutorial.php?summary=1');
      setTutorialSummary(res.data);
    } catch (e) {
      console.warn('Tutorial summary failed:', e);
      setTutorialSummary({ exists: false, title: 'Tutorial', tileCount: 0 });
    }
  };

  const openTutorial = async (start = false) => {
    window.location.href = '/aac/tutorial/';
  };
  // ⬆️ Tutorial helpers

  const createBoard = async () => {
    const limit = getBoardLimit();
    const totalBoards = Object.values(boardsByFolder).reduce((sum, list) => sum + list.length, 0);
    if (totalBoards >= limit) {
      showToast(`🚫 Board limit reached (${limit}). Upgrade your plan to add more.`, 'error', false);
      return;
    }
    if (!newBoardName.trim()) return;
    setCreatingBoard(true);
    try {
      const res = await api.post('/boards.php', { name: newBoardName.trim(), grid_config: { columns: 8, rows: 5 } });
      navigate(`/board/${res.data.id}/edit`);
    } catch (err) {
      console.error('Error creating board:', err);
    } finally {
      setNewBoardName('');
      setCreatingBoard(false);
      fetchFoldersAndBoards();
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/folders.php', { name: newFolderName.trim() });
      setNewFolderName('');
      fetchFoldersAndBoards();
      showToast('Folder successfully created', 'info', false);
    } catch (err) {
      console.error('Error creating folder:', err);
      showToast('Failed to create folder', 'error', false);
    }
  };

  const toggleExpand = (folderId) => setExpanded(prev => ({ ...prev, [folderId]: !prev[folderId] }));

  const enqueueOrderedSave = (work) => {
    // Serialise reorder writes with any previous reorder save. This prevents a
    // second drag from opening another parallel set of API/DB connections.
    orderSaveChainRef.current = orderSaveChainRef.current
      .catch(() => {})
      .then(work);
  };

  const flushFolderOrder = () => {
    if (folderOrderTimerRef.current) {
      window.clearTimeout(folderOrderTimerRef.current);
      folderOrderTimerRef.current = null;
    }

    if (pendingFolderOrderRef.current.size === 0) return;

    const changes = Array.from(pendingFolderOrderRef.current.values());
    pendingFolderOrderRef.current.clear();

    enqueueOrderedSave(async () => {
    try {
        for (let i = 0; i < changes.length; i += 1) {
          const change = changes[i];
          await api.put('/folders.php', {
            id: change.id,
            sort_order: change.sort_order,
          });

          // Hosting has a low new-connection-per-second ceiling. A small gap
          // keeps even very large reorder operations comfortably below it.
          if (i < changes.length - 1) {
            await wait(ORDER_SAVE_REQUEST_GAP_MS);
          }
      }
    } catch (err) {
      console.error('Failed to save folder positions:', err);
        showToast('Could not save folder order. Please try again.', 'error', false);
      }
    });
  };

  const scheduleFolderOrderSave = (before, after) => {
    const oldPositionById = new Map(
      before.map((folder, index) => [String(folder.id), index])
    );

    after.forEach((folder, index) => {
      if (oldPositionById.get(String(folder.id)) !== index) {
        pendingFolderOrderRef.current.set(String(folder.id), {
          id: folder.id,
          sort_order: index,
        });
      }
    });

    if (folderOrderTimerRef.current) {
      window.clearTimeout(folderOrderTimerRef.current);
    }

    folderOrderTimerRef.current = window.setTimeout(
      flushFolderOrder,
      ORDER_SAVE_DEBOUNCE_MS
    );
  };

  const flushBoardOrder = () => {
    if (boardOrderTimerRef.current) {
      window.clearTimeout(boardOrderTimerRef.current);
      boardOrderTimerRef.current = null;
    }

    if (pendingBoardOrderRef.current.size === 0) return;

    const changes = Array.from(pendingBoardOrderRef.current.values());
    pendingBoardOrderRef.current.clear();

    enqueueOrderedSave(async () => {
      try {
        for (let i = 0; i < changes.length; i += 1) {
          const change = changes[i];
          const payload = { position: change.position };

          if (change.folderChanged) {
            payload.folder_id = change.folder_id;
          }

          await api.put(`/boards.php?id=${change.id}`, payload);

          if (i < changes.length - 1) {
            await wait(ORDER_SAVE_REQUEST_GAP_MS);
          }
        }
      } catch (err) {
        console.error('Failed to save board positions:', err);
        showToast('Could not save board order. Please try again.', 'error', false);
      }
    });
  };

  const queueBoardOrderChanges = (beforeLists, afterLists) => {
    const beforeById = new Map();

    Object.entries(beforeLists).forEach(([folderKey, list]) => {
      (list || []).forEach((board, index) => {
        beforeById.set(String(board.id), {
          position: index,
          folder_id: folderKey === UNSORTED ? null : folderKey,
        });
      });
    });

    Object.entries(afterLists).forEach(([folderKey, list]) => {
      const folderId = folderKey === UNSORTED ? null : folderKey;

      (list || []).forEach((board, index) => {
        const previous = beforeById.get(String(board.id));
        const folderChanged =
          !previous ||
          String(previous.folder_id ?? '') !== String(folderId ?? '');
        const positionChanged = !previous || previous.position !== index;

        if (folderChanged || positionChanged) {
          pendingBoardOrderRef.current.set(String(board.id), {
            id: board.id,
            position: index,
            folder_id: folderId,
            folderChanged,
          });
      }
      });
    });

    if (boardOrderTimerRef.current) {
      window.clearTimeout(boardOrderTimerRef.current);
    }

    boardOrderTimerRef.current = window.setTimeout(
      flushBoardOrder,
      ORDER_SAVE_DEBOUNCE_MS
    );
  };

  const moveFolder = (from, to) => {
    if (from === to) return;

    const before = [...folders];
    const updated = [...before];
    const [moved] = updated.splice(from, 1);
    if (!moved) return;

    updated.splice(to, 0, moved);

    // UI is immediate. Only the folders whose positions actually changed are
    // queued, and repeated hover movements overwrite/de-duplicate those rows.
    setFolders(updated);
    scheduleFolderOrderSave(before, updated);
  };

  const moveBoard = (fromFolderId, toFolderId, fromIndex, toIndex, boardId) => {
    if (fromFolderId === toFolderId) {
      const before = [...(boardsByFolder[fromFolderId] || [])];
      if (fromIndex < 0 || fromIndex >= before.length) return;

      const updated = [...before];
      const [moved] = updated.splice(fromIndex, 1);
      if (!moved) return;

      const safeToIndex = Math.min(Math.max(toIndex, 0), updated.length);
      updated.splice(safeToIndex, 0, moved);
      updated.forEach((board, index) => {
        board.position = index;
      });

      setBoardsByFolder((prev) => ({
        ...prev,
        [fromFolderId]: updated,
      }));

      // Compare just this folder before/after. During a long drag, subsequent
      // hover changes are merged into the same pending Map instead of creating
      // another network request loop.
      queueBoardOrderChanges(
        { [fromFolderId]: before },
        { [fromFolderId]: updated }
      );
      return;
    }

    const sourceBefore = [...(boardsByFolder[fromFolderId] || [])];
    const targetBefore = [...(boardsByFolder[toFolderId] || [])];

    if (fromIndex < 0 || fromIndex >= sourceBefore.length) return;

    const source = [...sourceBefore];
    const target = [...targetBefore];
      const [moved] = source.splice(fromIndex, 1);
    if (!moved) return;

    const destinationFolderId = toFolderId === UNSORTED ? null : toFolderId;
    moved.folder_id = destinationFolderId;

      const safeToIndex = Math.min(Math.max(toIndex, 0), target.length);
      target.splice(safeToIndex, 0, moved);

    source.forEach((board, index) => {
      board.position = index;
    });
    target.forEach((board, index) => {
      board.position = index;
    });

    setBoardsByFolder((prev) => ({
        ...prev,
        [fromFolderId]: source,
      [toFolderId]: target,
    }));

    queueBoardOrderChanges(
      {
        [fromFolderId]: sourceBefore,
        [toFolderId]: targetBefore,
      },
      {
        [fromFolderId]: source,
        [toFolderId]: target,
      }
    );
  };

  const allBoardsFlat = Object.values(boardsByFolder).flat();
  const allBoardIds = allBoardsFlat.map((board) => String(board.id));

  const toggleManageMode = () => {
    if (bulkBusy) return;
    setManageMode((current) => {
      if (current) {
        setSelectedBoardIds(new Set());
        setSelectedFolderIds(new Set());
        setShowMovePopup(false);
        setMoveTargetFolderId('');
      }
      return !current;
    });
  };

  const toggleBoardSelected = (boardId, checked) => {
    const id = String(boardId);

    setSelectedBoardIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

    // If a board is manually deselected, its parent folder should no longer be
    // treated as fully selected. If all boards become selected manually, mark it.
    const parentEntry = Object.entries(boardsByFolder).find(
      ([folderId, list]) =>
        folderId !== UNSORTED &&
        (list || []).some((board) => String(board.id) === id)
    );

    if (parentEntry) {
      const [folderId, list] = parentEntry;
      setSelectedFolderIds((prev) => {
        const next = new Set(prev);
        const resultingBoards = new Set(selectedBoardIds);
        if (checked) resultingBoards.add(id);
        else resultingBoards.delete(id);

        const allInFolderSelected =
          list.length > 0 &&
          list.every((board) => resultingBoards.has(String(board.id)));

        if (allInFolderSelected) next.add(String(folderId));
        else next.delete(String(folderId));
        return next;
      });
    }
  };

  const toggleFolderSelected = (folderId, checked) => {
    const id = String(folderId);
    const folderBoards = boardsByFolder[folderId] || [];

    setSelectedFolderIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

    setSelectedBoardIds((prev) => {
      const next = new Set(prev);
      folderBoards.forEach((board) => {
        const boardId = String(board.id);
        if (checked) next.add(boardId);
        else next.delete(boardId);
      });
      return next;
    });
  };

  const allBoardsSelected =
    allBoardIds.length > 0 &&
    allBoardIds.every((id) => selectedBoardIds.has(id));

  const toggleSelectAll = () => {
    if (allBoardsSelected) {
      setSelectedBoardIds(new Set());
      setSelectedFolderIds(new Set());
      return;
    }

    setSelectedBoardIds(new Set(allBoardIds));
    setSelectedFolderIds(new Set(folders.map((folder) => String(folder.id))));
  };

  const invalidateBulkBoardCaches = async () => {
    if (!navigator.onLine) return;
    try {
      const cache = await caches.open('api-cache');
      await Promise.all([
        cache.delete('/aac/api/boards.php'),
        cache.delete('/aac/api/folders.php'),
        cache.delete('/aac/api/offline-sync.php'),
      ]);
    } catch {}
  };

  const openMoveSelected = () => {
    if (bulkBusy || selectedBoardIds.size === 0) return;

    if (folders.length === 0) {
      showToast('Create a folder before moving boards.', 'info', false);
      return;
    }

    setMoveTargetFolderId('');
    setShowMovePopup(true);
  };

  const handleMoveSelected = async () => {
    if (bulkBusy || selectedBoardIds.size === 0 || !moveTargetFolderId) return;

    const targetFolder = folders.find(
      (folder) => String(folder.id) === String(moveTargetFolderId)
    );
    if (!targetFolder) {
      showToast('Please choose a folder.', 'error', false);
      return;
    }

    const selectedIds = Array.from(selectedBoardIds);
    const selectedSet = new Set(selectedIds.map(String));
    const targetKey = String(targetFolder.id);

    // Boards already in the destination already satisfy the requested move, so
    // leave them exactly where they are rather than needlessly rewriting them.
    const boardsToMove = allBoardsFlat.filter(
      (board) =>
        selectedSet.has(String(board.id)) &&
        String(board.folder_id ?? '') !== targetKey
    );

    if (boardsToMove.length === 0) {
      setShowMovePopup(false);
      setMoveTargetFolderId('');
      showToast(
        `All selected boards are already in ${targetFolder.name}.`,
        'info',
        false
      );
      return;
    }

    // Append moved boards after the destination's existing boards. Using the
    // highest stored position avoids collisions even if earlier deletes left gaps.
    const existingTargetBoards = (boardsByFolder[targetFolder.id] || []).filter(
      (board) => !selectedSet.has(String(board.id))
    );
    const highestTargetPosition = existingTargetBoards.reduce(
      (highest, board) => Math.max(highest, Number(board.position ?? -1)),
      -1
    );

    setBulkBusy(true);
    try {
      for (let i = 0; i < boardsToMove.length; i += 1) {
        const board = boardsToMove[i];

        await api.put(`/boards.php?id=${board.id}`, {
          folder_id: targetFolder.id,
          position: highestTargetPosition + 1 + i,
        });

        if (i < boardsToMove.length - 1) {
          await wait(ORDER_SAVE_REQUEST_GAP_MS);
        }
      }

      await invalidateBulkBoardCaches();

      setShowMovePopup(false);
      setMoveTargetFolderId('');
      setSelectedBoardIds(new Set());
      setSelectedFolderIds(new Set());

      showToast(
        `${boardsToMove.length} board${boardsToMove.length === 1 ? '' : 's'} moved to ${targetFolder.name}.`,
        'success',
        false
      );

      await fetchFoldersAndBoards();
    } catch (err) {
      console.error('Bulk move failed:', err);
      showToast(
        err?.response?.data?.error || 'Some selected boards could not be moved.',
        'error',
        false
      );
      await fetchFoldersAndBoards();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (bulkBusy || selectedBoardIds.size === 0) return;

    const boardIds = Array.from(selectedBoardIds);
    const folderIds = Array.from(selectedFolderIds);

    const folderText = folderIds.length
      ? ` and ${folderIds.length} selected folder${folderIds.length === 1 ? '' : 's'}`
      : '';

    const confirmed = await showConfirm(
      `Delete ${boardIds.length} selected board${boardIds.length === 1 ? '' : 's'}${folderText}? This cannot be undone.`
    );
    if (!confirmed) return;

    setBulkBusy(true);
    try {
      // Keep the write rate intentionally conservative for shared hosting.
      for (let i = 0; i < boardIds.length; i += 1) {
        await api.delete(`/boards.php?id=${boardIds[i]}`);
        await clearBoardCaches(boardIds[i]);
        if (i < boardIds.length - 1) await wait(ORDER_SAVE_REQUEST_GAP_MS);
      }

      // Delete selected folder shells only after their selected boards are gone.
      for (let i = 0; i < folderIds.length; i += 1) {
        await api.delete(`/folders.php?id=${folderIds[i]}`);
        if (i < folderIds.length - 1) await wait(ORDER_SAVE_REQUEST_GAP_MS);
      }

      await invalidateBulkBoardCaches();
      window.dispatchEvent(new CustomEvent('sticky-core-changed'));

      setSelectedBoardIds(new Set());
      setSelectedFolderIds(new Set());
      showToast(
        `${boardIds.length} board${boardIds.length === 1 ? '' : 's'} deleted.`,
        'success',
        false
      );
      await fetchFoldersAndBoards();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      showToast(
        err?.response?.data?.error || 'Some selected items could not be deleted.',
        'error',
        false
      );
      await fetchFoldersAndBoards();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDuplicateSelected = async () => {
    if (bulkBusy || selectedBoardIds.size === 0) return;

    const boardIds = Array.from(selectedBoardIds);
    const availableSlots = Math.max(0, getBoardLimit() - allBoardsFlat.length);

    if (boardIds.length > availableSlots) {
      showToast(
        `You can only duplicate ${availableSlots} more board${availableSlots === 1 ? '' : 's'} before reaching your plan limit.`,
        'error',
        false
      );
      return;
    }

    const confirmed = await showConfirm(
      `Duplicate ${boardIds.length} selected board${boardIds.length === 1 ? '' : 's'}?`
    );
    if (!confirmed) return;

    setBulkBusy(true);
    try {
      for (let i = 0; i < boardIds.length; i += 1) {
        await api.post(`/boards.php?duplicate=${boardIds[i]}`);
        if (i < boardIds.length - 1) await wait(ORDER_SAVE_REQUEST_GAP_MS);
      }

      await invalidateBulkBoardCaches();
      setSelectedBoardIds(new Set());
      setSelectedFolderIds(new Set());

      showToast(
        `${boardIds.length} board${boardIds.length === 1 ? '' : 's'} duplicated.`,
        'success',
        false
      );
      await fetchFoldersAndBoards();
    } catch (err) {
      console.error('Bulk duplicate failed:', err);
      showToast(
        err?.response?.data?.error || 'Some selected boards could not be duplicated.',
        'error',
        false
      );
      await fetchFoldersAndBoards();
    } finally {
      setBulkBusy(false);
    }
  };

  const limit = getBoardLimit();
  const totalBoards = Object.values(boardsByFolder).reduce((sum, list) => sum + list.length, 0);

  const hasFolders = folders.length > 0;

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ padding: '1rem', maxWidth: '600px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Your Boards</h2>
        <h2 style={{ fontSize: '1rem', marginBottom: '1rem' }}>
          You've used {totalBoards}/{limit} boards. {totalBoards >= limit ? '(Upgrade your plan to add more.)' : ''}
        </h2>

        {/* First-login Tutorial prompt (non-blocking) */}
        {showTutorialPrompt && (
          <div style={promptStyles.backdrop}>
            <div style={promptStyles.modal}>
              <h3 style={{ marginTop: 0 }}>Take a quick tutorial?</h3>
              <p style={{ margin: '8px 0 16px' }}>Learn the basics in under 2 minutes.</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { localStorage.setItem(seenKey, '1'); setShowTutorialPrompt(false); openTutorial(true); }}
                  style={buttonStyle('#007bff')}
                >
                  Yes, guide me
                </button>
                <button
                  onClick={() => { localStorage.setItem(seenKey, '1'); setShowTutorialPrompt(false); }}
                  style={buttonStyle('#6c757d')}
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Folder / Board */}
        <div style={{ display: 'flex', marginBottom: '0.75rem', gap: '10px' }}>
          <input
            type="text"
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <button
            onClick={createFolder}
            style={{ marginLeft: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Add Folder
          </button>
          {/* <input
            type="text"
            placeholder="Board name"
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', marginLeft: '0.5rem' }}
          /> */}
          <button
            onClick={() => setShowPopup(true)}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Add Board
          </button>

          <button
            onClick={() => navigate('/smart-setup')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6f42c1',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 600
            }}
            title="Create a personalised board"
          >
            ✨ Create with AI
          </button>
        </div>

        <StickyCoreSettings onOpen={openBoard} />

        {/* Boards/Folders region with overlay spinner */}
        <div style={{ position: 'relative', minHeight: 160 }}>
          <LoadingOverlay show={loading} text="Loading boards…" />

        {/* Fixed Tutorial card (non-draggable, non-deletable) */}
        {<TutorialCard
            isCached={tutorialIsCached}
            onOpen={() => navigate('/tutorial')}
        />}

        {/* Fixed Community Boards card (non-draggable, non-deletable) */}
        {<CommunityBoards
            onOpen={() => navigate('/community')}
        />}

        {<PresetBoardsCard
            onOpen={() => navigate('/presets')}
        />}

          {/* Unsorted Boards */}
          <UnsortedDropZone
            onDropBoard={(fromId, toId, fromIndex, boardId) =>
              moveBoard(fromId, toId, fromIndex, (boardsByFolder[UNSORTED] || []).length, boardId)
            }
            manageMode={manageMode}
            onToggleManage={toggleManageMode}
            selectedCount={selectedBoardIds.size}
            allSelected={allBoardsSelected}
            onToggleSelectAll={toggleSelectAll}
            onDeleteSelected={handleDeleteSelected}
            onDuplicateSelected={handleDuplicateSelected}
            onMoveSelected={openMoveSelected}
            canMoveSelected={folders.length > 0}
            bulkBusy={bulkBusy}
          >
            {(boardsByFolder[UNSORTED] || []).map((board, i) => (
              <Board
                key={board.id}
                board={board}
                index={i}
                folderId={UNSORTED}
                moveBoard={moveBoard}
                onClickUse={openBoard}
                onClickEdit={(id) => navigate(`/board/${id}/edit`)}
                fetchBoards={fetchFoldersAndBoards}
                boardsByFolder={boardsByFolder}
                setBoardsByFolder={setBoardsByFolder}
                manageMode={manageMode}
                selected={selectedBoardIds.has(String(board.id))}
                onToggleSelected={toggleBoardSelected}
              />
            ))}
          </UnsortedDropZone>

          {/* Folders */}
          {hasFolders ? (
            <>
              <h3 style={{ margin: '1rem 0' }}>Folders</h3>
              {folders.map((folder, index) => {
                const boardsInFolder = boardsByFolder[folder.id] || [];
                const boardNames = boardsInFolder.map(b => b.name || `Board #${b.id}`);
                return (
                  <Folder
                    key={folder.id}
                    folder={folder}
                    index={index}
                    moveFolder={moveFolder}
                    toggleExpand={toggleExpand}
                    isExpanded={!!expanded[folder.id]}
                    onDropBoard={(fromId, toId, fromIndex, boardId) =>
                      moveBoard(fromId, toId, fromIndex, (boardsByFolder[toId] || []).length, boardId)
                    }
                    boardCountInFolder={boardsInFolder.length}
                    fetchBoards={fetchFoldersAndBoards}
                    boardNames={boardNames}
                    manageMode={manageMode}
                    selected={
                      boardsInFolder.length > 0 &&
                      boardsInFolder.every((board) => selectedBoardIds.has(String(board.id)))
                    }
                    someSelected={boardsInFolder.some((board) => selectedBoardIds.has(String(board.id)))}
                    onToggleSelected={toggleFolderSelected}
                  >
                    {expanded[folder.id] && boardsInFolder.map((board, i) => (
                      <Board
                        key={board.id}
                        board={board}
                        index={i}
                        folderId={folder.id}
                        moveBoard={moveBoard}
                        onClickUse={openBoard}
                        onClickEdit={(id) => navigate(`/board/${id}/edit`)}
                        fetchBoards={fetchFoldersAndBoards}
                        boardsByFolder={boardsByFolder}
                        setBoardsByFolder={setBoardsByFolder}
                        manageMode={manageMode}
                        selected={selectedBoardIds.has(String(board.id))}
                        onToggleSelected={toggleBoardSelected}
                      />
                    ))}
                  </Folder>
                );
              })}
            </>
          ) : (
            // No folders: no orphan heading; show a helpful hint
            <div style={{ marginTop: '1.5rem', color: '#666' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>No folders yet</div>
              <div>Create one above to organise your boards.</div>
            </div>
          )}
        </div>
      </div>

        {showMovePopup && (
          <div
            role="presentation"
            onMouseDown={() => {
              if (!bulkBusy) {
                setShowMovePopup(false);
                setMoveTargetFolderId('');
              }
            }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 6500,
              background: 'rgba(15, 23, 42, 0.42)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="move-selected-title"
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                width: 'min(460px, 94vw)',
                maxHeight: '78vh',
                background: '#fff',
                borderRadius: 14,
                boxShadow: '0 20px 55px rgba(15, 23, 42, 0.24)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  padding: '16px 18px 14px',
                  borderBottom: '1px solid #e7ebef',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 14,
                }}
              >
                <div>
                  <div
                    id="move-selected-title"
                    style={{ fontSize: 18, fontWeight: 800, color: '#1f2937' }}
                  >
                    Move selected boards
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 13,
                      color: '#6b7280',
                      lineHeight: 1.4,
                    }}
                  >
                    Choose the folder for {selectedBoardIds.size} selected board{selectedBoardIds.size === 1 ? '' : 's'}.
                  </div>
                </div>

                <button
                  type="button"
                  disabled={bulkBusy}
                  onClick={() => {
                    setShowMovePopup(false);
                    setMoveTargetFolderId('');
                  }}
                  aria-label="Close move boards"
                  style={{
                    border: 0,
                    background: 'transparent',
                    color: '#7b8490',
                    fontSize: 24,
                    lineHeight: 1,
                    cursor: bulkBusy ? 'not-allowed' : 'pointer',
                    padding: 2,
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ overflowY: 'auto', padding: 12 }}>
                {folders.map((folder) => {
                  const selected =
                    String(moveTargetFolderId) === String(folder.id);
                  const boardCount = (boardsByFolder[folder.id] || []).length;

                  return (
                    <button
                      key={folder.id}
                      type="button"
                      disabled={bulkBusy}
                      onClick={() => setMoveTargetFolderId(String(folder.id))}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 13px',
                        marginBottom: 8,
                        borderRadius: 10,
                        border: selected
                          ? '1px solid #8bb6a4'
                          : '1px solid #dde3e8',
                        background: selected ? '#f0f7f4' : '#fff',
                        color: '#26323d',
                        cursor: bulkBusy ? 'not-allowed' : 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          border: selected
                            ? '5px solid #2f7d61'
                            : '2px solid #b8c1ca',
                          background: '#fff',
                          boxSizing: 'border-box',
                          flex: '0 0 auto',
                        }}
                      />
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 14,
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {folder.name}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            marginTop: 2,
                            fontSize: 12,
                            color: '#7a838d',
                          }}
                        >
                          {boardCount} board{boardCount === 1 ? '' : 's'}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div
                style={{
                  padding: '12px 16px 16px',
                  borderTop: '1px solid #e7ebef',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                  background: '#fbfcfd',
                }}
              >
                <button
                  type="button"
                  disabled={bulkBusy}
                  onClick={() => {
                    setShowMovePopup(false);
                    setMoveTargetFolderId('');
                  }}
                  style={{
                    padding: '9px 13px',
                    borderRadius: 9,
                    border: '1px solid #d5dbe1',
                    background: '#fff',
                    color: '#46515c',
                    fontWeight: 700,
                    cursor: bulkBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={bulkBusy || !moveTargetFolderId}
                  onClick={handleMoveSelected}
                  style={{
                    padding: '9px 15px',
                    borderRadius: 9,
                    border: '1px solid',
                    borderColor:
                      moveTargetFolderId && !bulkBusy ? '#276b54' : '#d8dde2',
                    background:
                      moveTargetFolderId && !bulkBusy ? '#2f7d61' : '#eef0f2',
                    color:
                      moveTargetFolderId && !bulkBusy ? '#fff' : '#9aa1a8',
                    fontWeight: 750,
                    cursor:
                      moveTargetFolderId && !bulkBusy
                        ? 'pointer'
                        : 'not-allowed',
                  }}
                >
                  {bulkBusy
                    ? 'Moving…'
                    : `Move ${selectedBoardIds.size} board${selectedBoardIds.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Popup goes here */}
        <Popup
          isOpen={showPopup}
          onClose={() => setShowPopup(false)}
          availableBoards={Object.values(boardsByFolder).flat()}
          onSubmit={async (boardData) => {
            setShowPopup(false);
            const usage = await getBoardUsage();
              if (usage.isLimitReached) {
                showToast(`Board limit reached for your plan, You have used ${usage.count} of ${usage.limit} boards.`, "error");
                return;
              }
            const createRes = await api.post('/boards.php', boardData);

            if (boardData.is_core_board && createRes?.data?.id) {
              await api.put('/core-settings.php', {
                action: 'set_board_core',
                board_id: Number(createRes.data.id),
                is_core_board: true,
                core_default_board_id: boardData.core_default_board_id || null,
              });
              window.dispatchEvent(new CustomEvent('sticky-core-changed'));
            }

            if (navigator.onLine) {
              try {
                const cache = await caches.open('api-cache');
                await Promise.all([
                  cache.delete('/aac/api/boards.php'),
                  cache.delete('/aac/api/folders.php'),
                ]);
              } catch{}
            }

            fetchFoldersAndBoards();
          }}
        />
    </DndProvider>
  );
};

const manageActionStyle = (background, color, borderColor) => ({
  padding: '7px 11px',
  background,
  color,
  border: `1px solid ${borderColor}`,
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

const buttonStyle = (bgColor, textColor = '#fff') => ({
  padding: '0.3rem 0.75rem',
  backgroundColor: bgColor,
  color: textColor,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer'
});

const overlayStyles = {
  container: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(255,255,255,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6
  },
  card: {
    background: '#fff',
    padding: 16,
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
    textAlign: 'center'
  }
};

// Lightweight inline modal styles for the first-login tutorial prompt
const promptStyles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    width: 'min(90vw, 420px)',
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 12px 28px rgba(0,0,0,0.25)',
    padding: '16px 16px 12px'
  }
};

export default BoardFolderList;
